<template>
  <form v-on:submit.prevent="submitRegistration" id="registration-form" autocomplete="off" v-cloak>
    <div class="row justify-content-center">
      <div :class="['col-12', !withSidebar ? 'col-md-10 col-lg-8 col-xl-7' : '' ]">
        <slot></slot>
      </div>
    </div>
    <div :class="['row mt-4' , !withSidebar ? 'justify-content-center' : '']">
      <div :class="['col-12 mb-3', withSidebar ? 'col-md-7 order-1 order-md-0' : 'col-md-10 col-lg-8 col-xl-7']">

        <!-- Account -->
        <div v-if="accountEnabled && !account && !isGuestMode">
          <div class="">
            <h5>{{ $t("Already have an account") }}</h5>

            <slot name="login-form"></slot>

          </div>
          <div v-if="accountRegistrationAvailable" class="mt-4">
            <hr>
            <h5>{{ $t("Create an account") }}</h5>

            <slot name="create-account"></slot>

          </div>
          <div v-if="!accountRequired" class="mt-4">
            <hr>
            <h5>{{ $t("You do not want to create an account") }}</h5>

            <div class="form-group mt-4">
              <button type="button" class="btn btn-primary" @click="onClickGuestMode">
                {{ $t("Continue as guest") }}
              </button>
            </div>
          </div>
        </div>

        <!-- Registration fields -->
        <fieldset v-else :disabled="disabledRegistrationForm">
          <div v-if="account">
            <div class="mt-3 mb-3" v-if="!displayRegistrationForm">
              <div v-if="registrationsList.length > 0">
                <h5>{{ $t("Select an existing Registration") }}</h5>
                <FuseSearch
                  :data="registrationsList"
                  :keys="['firstname', 'lastname', 'email']"
                  :exact-match-number="0.4">
                  <template #search="{ modelValue, updateModelValue }">
                    <div class="my-3">
                      <FuseSearchInput
                        :modelValue="modelValue"
                        @update:modelValue="updateModelValue"
                        class="form-control mb-3"
                      />
                    </div>
                  </template>
                  <template #default="{ results }" class="registration-list">
                    <OptionValue v-for="registration in results"
                                 :key="registration.uuid"
                                 :option-value="registration.uuid"
                                 :selected="registration.uuid === registrationUuid"
                                 @change="registrationUuid = registration.uuid"
                                 class="btn-block text-left"
                    >
                      <div>
                        <a href class="no-underline" @click.prevent="registrationUuid = registration.uuid">
                          {{ registration.firstname }} {{ registration.lastname }}
                        </a>
                        <br>
                        <span class="text-muted">{{ registration.email }}</span>
                      </div>
                      <span v-if="registration.status.value === 'validated'"
                            class="badge bg-success text-white fs-8">{{ registration.status.label }}</span>
                      <span v-if="registration.status.value === 'registered'"
                            class="badge bg-warning text-white fs-6">{{ registration.status.label }}</span>
                    </OptionValue>
                  </template>
                </FuseSearch>
              </div>
              <div class="mt-3">
                <a href @click.prevent="onClickCreateNewRegistration">
                  <i class="fa fa-plus-circle"></i>
                  {{ $t("Add a new Registration") }}
                </a>
              </div>
            </div>
          </div>
          <div v-if="!account || displayRegistrationForm">
            <div v-if="account && registrationsList.length > 0">
              <a href @click.prevent="onClickSelectExistingRegistration">
                <i class="fa fa-arrow-left"></i>
                {{ $t("Select an existing Registration") }}
              </a>
            </div>
            <div v-for="field in visibleFields"
                 :key="field.key"
                 :class="{
                   'has-danger has-error':  registrationErrors.hasOwnProperty(field.key),
                   'form-check' : field.type === 'checkbox',
                 }"
                 class="form-group mt-3">

              <div v-if="field.type === 'radio'">
                <label :for="'registration_' + field.key">
                  {{ field.templateOptions.label }}
                  <small v-if="isFieldRequired(field)" class="align-middle font-weight-bold text-danger">*</small>
                </label>
                <div>
                  <label v-for="option in field.templateOptions.options" class="radio-inline mr-3">
                    <input type="radio"
                           :name="field.key"
                           v-bind:class="{'is-invalid': registrationErrors.hasOwnProperty(field.key) }"
                           :value="option.value"
                           v-model="registration[field.key]">
                    {{ option.name }}
                  </label>
                </div>
              </div>

              <div v-if="field.type === 'input'">
                <label :for="'registration_' + field.key">
                  {{ field.templateOptions.label }}
                  <small v-if="isFieldRequired(field)" class="align-middle font-weight-bold text-danger">*</small>
                </label>
                <input :type="field.templateOptions.type"
                       v-model="registration[field.key]"
                       :id="'registration_' + field.key"
                       :autocomplete="field.templateOptions.autocomplete"
                       :class="[
                             'form-control',
                             { 'is-invalid': registrationErrors.hasOwnProperty(field.key) }
                           ]"
                       :required="isFieldRequired(field)"
                       :placeholder="field.templateOptions.placeholder">
                <small v-if="field.templateOptions.text" class="form-text text-muted" v-html="field.templateOptions.text"></small>
              </div>

              <div v-if="field.type === 'birthday'">
                <label :for="'registration_' + field.key">
                  {{ field.templateOptions.label }}
                  <small v-if="isFieldRequired(field)" class="align-middle font-weight-bold text-danger">*</small>
                </label>
                <div class="row no-gutters">
                  <div class="col-3">
                    <input type="number" v-model="birthday.day"
                           id="registration_birthday"
                           min="1" max="31"
                           :class="[
                                'form-control',
                                { 'is-invalid': registrationErrors.hasOwnProperty(field.key) }
                             ]"
                           :placeholder="field.templateOptions.placeholder_day"
                           class="" />
                  </div>
                  <div class="col-6">
                    <select v-model="birthday.month"
                            :disabled="disabledRegistrationForm"
                            :class="[
                                  'form-control',
                                  { 'is-invalid': registrationErrors.hasOwnProperty(field.key) }
                               ]"
                    >
                      <option>----</option>
                      <option v-for="(month, index) in months"
                              :value="index">{{ month }}
                      </option>
                    </select>
                  </div>
                  <div class="col-3">
                    <input v-model="birthday.year"
                           type="number"
                           :min="new Date().getFullYear() - 100"
                           :max="new Date().getFullYear()"
                           :class="[
                                'form-control',
                                { 'is-invalid': registrationErrors.hasOwnProperty(field.key) }
                             ]"
                           :placeholder="field.templateOptions.placeholder_year"
                    />
                  </div>
                </div>
              </div>

              <div v-if="field.type === 'phone'">
                <label :for="'registration_' + field.key">
                  {{ field.templateOptions.label }}
                  <small v-if="isFieldRequired(field)" class="align-middle font-weight-bold text-danger">*</small>
                </label>
                <div class="row no-gutters">
                  <div class="col-4">
                    <Select2 style="width: 100%"
                             id="registration_phone"
                             v-model="registration.phone_country"
                             :disabled="disabledRegistrationForm"
                             v-cloak>
                      <option v-for="country in field.templateOptions.options"
                              :value="country.value"
                              :disabled="country.disabled"
                      >{{ country.name }}
                      </option>
                    </Select2>
                  </div>
                  <div class="col-8">
                    <input type="tel" name="phone" v-model="registration.phone" class="form-control"
                           :class="{ 'is-invalid': registrationErrors.hasOwnProperty('phone') }"
                           autocomplete="off" autocapitalize="off" spellcheck="false">
                  </div>
                </div>
              </div>

              <div v-if="field.type === 'textarea'">
                <label :for="'registration_' + field.key">
                  {{ field.templateOptions.label }}
                  <small v-if="isFieldRequired(field)"
                         class="align-middle font-weight-bold text-danger">*</small>
                </label>
                <textarea
                  v-model="registration[field.key]"
                  :id="'registration_' + field.key"
                  :class="[
                        'form-control',
                        { 'is-invalid': registrationErrors.hasOwnProperty(field.key) }
                      ]"
                  :required="isFieldRequired(field)"
                ></textarea>
              </div>

              <div v-if="field.type === 'select' && field.key !== 'stages'">
                <label :for="'registration_' + field.key">
                  {{ field.templateOptions.label }}
                  <small v-if="isFieldRequired(field)" class="align-middle font-weight-bold text-danger">*</small>
                </label>
                <Select2
                  v-model="registration[field.key]"
                  :id="'registration_' + field.key"
                  :disabled="disabledRegistrationForm"
                  style="width: 100%" v-cloak>
                  <option v-for="(option, i) in field.templateOptions.options"
                          :key="i"
                          :value="option.value"
                          :disabled="option.disabled">
                    {{ option.name }}
                  </option>
                </Select2>
                <small v-if="field.templateOptions.text" class="form-text text-muted" v-html="field.templateOptions.text"></small>
              </div>

              <template v-if="field.type === 'checkbox'">
                <input type="checkbox"
                       :class="[
                           'form-check-input',
                            { 'is-invalid': registrationErrors.hasOwnProperty(field.key) }
                         ]"
                       value="1"
                       :id="'registration_' + field.key"
                       v-model="registration[field.key]"
                >
                <label class="form-check-label"
                       :for="'registration_' + field.key">
                  {{ addDotIfMissing(field.templateOptions.label) }}
                  <a :href="field.templateOptions.link" v-if="field.templateOptions.link" target="_blank">
                    {{ field.templateOptions.linkTitle }}
                  </a>
                  <small v-if="isFieldRequired(field)" class="align-middle font-weight-bold text-danger">&nbsp;*</small>
                </label>
                <small v-if="field.templateOptions.text" class="form-text text-muted" v-html="field.templateOptions.text"></small>
              </template>

              <div v-if="field.type === 'multi-checkbox'">
                <label>
                  {{ field.templateOptions.label }}
                  <small v-if="isFieldRequired(field)" class="align-middle font-weight-bold text-danger">*</small>
                  <small v-if="field.templateOptions.helper" class="form-text text-muted" v-html="field.templateOptions.helper"></small>
                </label>
                <div v-if="field.templateOptions.text" class="text-muted" v-html="field.templateOptions.text"></div>
                <fieldset class="form-group">
                  <template v-for="option in field.templateOptions.options" :key="option.value">
                    <div :class="['form-check', field.key === 'stages' ? 'mb-1' : '']">
                      <input type="checkbox"
                             :id="'registration_' + field.key + '_' + String(option.value)"
                             v-model="registration[field.key]"
                             :value="String(option.value)"
                             class="form-check-input">
                      <label :for="'registration_' + field.key + '_' + String(option.value)"
                             class="form-check-label">
                        <template v-if="field.key === 'stages'">
                          <StageInformation :stage="stages.find((s) => String(s.id) === String(option.value))">
                            <template #default="{stageName, formattedDate}">
                              <strong>{{ stageName }}</strong> - <span>{{ formattedDate }}</span>
                            </template>
                            <template #location="{location}">
                              <div class="text-muted">{{ location.address }}</div>
                            </template>
                            <template #quota="{quota}">
                              <div>
                                <div class="d-flex align-items-center text-muted">
                                  <svg height="10"
                                       width="10"
                                       xmlns="http://www.w3.org/2000/svg"
                                       class="mr-1"
                                  >
                                    <circle r="4" cx="5" cy="5"
                                            :fill="quota.percentage_used > 80 ? '#A12A2A' : (quota.percentage_used > 50 ? '#BFA15A' : '#2F6F5E')" />
                                  </svg>
                                  <strong class="mr-1">{{ quota.remaining }}</strong>
                                  <span>{{ $t("remaining place", quota.remaining) }}</span>
                                </div>
                              </div>
                            </template>
                          </StageInformation>
                        </template>
                        <template v-else>
                          {{ option.name }}
                        </template>
                      </label>
                      <small v-if="option.help" class="form-text text-muted" v-text="option.help"></small>
                    </div>
                  </template>
                </fieldset>
              </div>

              <div v-if="field.type === 'din'">
                <DinSetting
                  :field="field"
                  :registration="registration"
                  :errors="registrationErrors"
                  @input="onDinSettingChange"
                />
                <DinValue
                  :field="field"
                  :registration="registration"
                  @dinUpdated="onDinSettingChange"
                ></DinValue>
              </div>

              <div v-if="field.type === 'signature'">
                <label :for="'registration_' + field.key">
                  {{ field.templateOptions.label }}
                  <small v-if="isFieldRequired(field)" class="align-middle font-weight-bold text-danger">*</small>
                </label>
                <div v-if="field.templateOptions.text"
                     v-html="field.templateOptions.text"
                     class="mb-3 text-muted p-1"
                     style="max-height: 200px; overflow-y: scroll; background-color: #fCfCfC; cursor: zoom-in; font-size: 0.875em /* similar to small tag */"
                     @click="showModalField = field.key"
                >
                </div>
                <Modal size="lg"
                       :title="field.templateOptions.label"
                       :show="showModalField === field.key"
                       @hide="showModalField = null"
                       @hidePrevented="showModalField = null"
                >
                  <div v-if="field.templateOptions.text"
                       v-html="field.templateOptions.text"
                       style="max-height: 600px; overflow-y: scroll;"
                  />
                </Modal>
                <div class="row">
                  <div class="col col-md-9 col-lg-7">
                    <SignaturePad v-model="registration[field.key]" height="150px"></SignaturePad>
                  </div>
                </div>
                <div v-if="field.templateOptions.helper" class="mt-2" v-html="field.templateOptions.helper"></div>
              </div>

              <div class="invalid-feedback"
                 v-if="registrationErrors.hasOwnProperty(field.key)"
                 v-bind:class="{'d-block': registrationErrors.hasOwnProperty(field.key) }"
                 v-cloak>
                {{ registrationErrors[field.key]?.[0] }}
              </div>

            </div><!-- end v-for of fieldsConfig -->

            <div class="form-group form-check">
                <input id="frm-registration_consent" type="checkbox" v-model="registration.consent" value="1" class="form-check-input" required />
                <label for="frm-registration_consent" class="form-check-label">
                  {{ $t("I accept the terms of service and confirm that I have read the privacy policy") }}.
                  <a :href="privacyPolicyUrl" target="_blank">
                    {{ $t("View privacy policy") }}
                  </a>
                  <small class="align-middle font-weight-bold text-danger">&nbsp;*</small>
                </label>
                <div class="invalid-feedback" :class="{'d-block' : registrationErrors.hasOwnProperty('consent')}"
                     v-if="registrationErrors.hasOwnProperty('consent')" v-cloak>
                  {{ registrationErrors.consent[0] }}
                </div>
            </div>
          </div>
        </fieldset>

        <!-- end of registration form (optin / legal / and submit button) -->
        <div v-if="(!accountEnabled) || account || (!accountRequired && isGuestMode)">
          <div class="form-group">
            <div class="alert alert-danger" role="alert" v-if="registrationErrors.hasOwnProperty('unknown')" v-cloak>
              {{ registrationErrors.unknown[0] }}
            </div>
            <button type="submit" class="btn btn-primary btn-lg" :disabled="disabledSubmitButton">
              <span v-if="status" class="d-flex">
                  <tiny-spinner class="mr-3" style="width: 20px"></tiny-spinner>
                  {{ status }}
              </span>
              <span v-else-if="booking">
                {{ $t('Confirm my booking') }}
              </span>
              <span v-else>
                {{ $t("Confirm my registration") }}
              </span>
            </button>
            <small class="text-muted mt-3 d-block">
              <span class="align-middle font-weight-bold text-danger">*</span>
              {{ $t("Required fields") }}
            </small>
            <!--
              Google allows the reCAPTCHA badge to be hidden only while its branding stays visible
              in the flow, so this notice is what pays for the hidden badge. Do not remove it
              without bringing the badge back.
            -->
            <small v-if="recaptchaEnabled" class="text-muted mt-2 d-block">
              {{ $t("This site is protected by reCAPTCHA.") }}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                {{ $t("Google Privacy Policy") }}
              </a>
              &middot;
              <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">
                {{ $t("Google Terms of Service") }}
              </a>
            </small>
          </div>
        </div>
      </div>
      <!---  Side barre ---->
      <div v-if="withSidebar && !(accountEnabled && !account && !isGuestMode) " class="order-0 order-lg-1 col-12 col-md-5 mb-3">
        <slot name="booking" v-if="booking" v-bind:booking="booking">

        </slot>
        <fieldset class="card" v-else-if="fieldsConfig.find((field) => field.key === 'stages' && field.type === 'select')" :disabled="disabledRegistrationForm">
          <div class="card-body">
            <StagesSelector
              v-model="registration.stages"
              :stages="stagesFormated"
              :required="fieldsConfig.find((field) => field.key === 'stages')?.templateOptions?.required"
              :disabled="disabledRegistrationForm"
              :error="registrationErrors?.['stages']?.[0]"
            ></StagesSelector>
            <template v-if="stagesSelected.length > 0">
              <hr>
              <StageInformation
                :stage="stagesSelected[0]"
                inline-map
              />
            </template>
          </div>
        </fieldset><!-- /stage -->
      </div>

    </div>
  </form>
</template>

<script setup>
import Select2 from "@/components/frontend/ui/Select2.vue";
import DinSetting from "@/components/frontend/form-components/DinSetting.vue";
import DinValue from "@/components/frontend/form-components/DinValue.vue";
import TinySpinner from "@/components/frontend/ui/Spinner.vue";
import OptionValue from "@/components/frontend/ui/OptionValue.vue";
import SignaturePad from "@/components/frontend/signature/SignaturePad.vue";
import Modal from "@/components/frontend/ui/Modal.vue";
import StageInformation from "@/components/frontend/stage/StageInformation.vue";
import StagesSelector from "@/components/frontend/registrations/StagesSelector.vue";
import FuseSearch from "@/components/atoms/fuse-search/FuseSearch.vue";
import FuseSearchInput from "@/components/atoms/fuse-search/FuseSearchInput.vue";
import { computed, onMounted, ref, watch } from "vue";
import { useRegistrationAnswers } from "@/composables/useRegistrationAnswers";
import { useBirthdayInput } from "@/composables/useBirthdayInput";
import { useAccountRegistrations } from "@/composables/useAccountRegistrations";
import { useFieldConditions } from "@/composables/useFieldConditions";

const props = defineProps({
  account: {
    type: Object,
    default: null
  },
  accountUrl: {
    type: String,
    default: null
  },
  accountEnabled: {
    type: Boolean,
    default: false
  },
  accountRequired: {
    type: Boolean,
    default: false
  },
  accountRegistrationAvailable: {
    type: Boolean,
    default: false
  },
  status: {
    default: null
  },
  fieldsConfig: {
    required: true
  },
  registrationErrors: {
    default: () => ({})
  },
  optinLabel: {
    default: ""
  },
  /** Drives the reCAPTCHA branding notice, which is what lets the badge stay hidden. */
  recaptchaEnabled: {
    type: Boolean,
    default: false
  },
  stages: {
    type: Array,
    default: () => []
  },
  stage: {
    default: null
  },
  locale: String,
  termsAndConditionsUrl: String,
  privacyPolicyUrl: String,
  withSidebar: {
    type: Boolean,
    default: false
  },
  booking: {
    type: Object,
    default: undefined
  },
  stripe: {
    default: null
  },
  disabledRegistrationForm: {
    type: Boolean,
    default: false
  },
  disabledSubmit: {
    type: Boolean,
    default: false
  },
  tour: {
    default: null
  }
});

const emit = defineEmits(["submitRegistration"]);

const { answers: registration, merge: mergeAnswers } = useRegistrationAnswers(
  () => props.fieldsConfig,
  () => props.account,
);

const { birthday, date: birthdayDate, months } = useBirthdayInput(() => props.locale);

// Only overwrite the answer once the three parts make a real date; clearing one
// of them leaves the birthday already given untouched.
watch(birthdayDate, (date) => {
  if (date) {
    registration.value.birthday = date;
  }
});

const {
  registrations: registrationsList,
  selectedUuid: registrationUuid,
  showForm: displayRegistrationForm,
  isGuest: isGuestMode,
  fetch: getRegistrations,
  createNew: onClickCreateNewRegistration,
  selectExisting: onClickSelectExistingRegistration,
  selected: selectedRegistration,
} = useAccountRegistrations(() => props.tour, () => props.locale);

const { visibleFields, isRequired: isFieldRequired } = useFieldConditions(
  () => props.fieldsConfig,
  () => registration.value,
);

const card = ref(null);
const showModalField = ref(null);

const stagesFormated = computed(() => props.stages.map((stage) => ({
  ...stage,
  text: stage.name
})));

const disabledSubmitButton = computed(() => props.disabledSubmit);

const stagesSelected = computed(() =>
  props.stages.filter((stage) => !!registration.value.stages?.find((id) => parseInt(id) === parseInt(stage.id)))
);

const addDotIfMissing = (str) => str.endsWith('.') ? str : str + '.';

const onClickGuestMode = () => {
  isGuestMode.value = true;
};

const onDinSettingChange = (answers) => {
  mergeAnswers(answers);
};

const submitRegistration = () => {
  const answers = !displayRegistrationForm.value && registrationUuid.value
    ? selectedRegistration()
    : { ...registration.value };

  emit("submitRegistration", {
    registration: {
      ...answers,
      booking: props.booking ? props.booking.uuid : null, // add booking to registration rules for
    },
    card: card.value
  });
};

onMounted(() => {
  if (props.stage) {
    registration.value.stages = [props.stage];
  }
  if (props.stages.length === 1 && props.fieldsConfig.find(field => field.key === "stages")?.templateOptions.required && !registration.value.stages) {
    registration.value.stages = [
      props.stages[0]?.id?.toString()
    ];
  }
  if (props.tour) {
    getRegistrations();
  }
});
</script>

<style scoped>
.no-underline {
  text-decoration: none;
}
 .registration-list {
   max-height: 300px;
   overflow-y: scroll;
 }
</style>
